import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Copy,
  Cpu,
  Download,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Lightbulb,
  LifeBuoy,
  Pencil,
  Play,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  PowerOff,
  Trash2,
  Video,
  Wifi,
  X,
} from "lucide-react";
import {
  apiAgentCreateToken,
  apiAgentDeleteToken,
  apiAgentListTokens,
  apiAgentRegister,
  apiAgentRegisterExisting,
  apiAgentRenameToken,
  apiAgentSetTokenActive,
  apiAgentStatus,
} from "../api/api";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import PageHeader from "../components/shell/PageHeader";
import { helpRoute } from "./help/helpTopics";
import { SUPPORT_WHATSAPP, WhatsAppLogo, buildWhatsAppHref } from "./help/helpUi";

const LS_TOKEN = "agent_token";
const LS_TOKEN_OWNER = "agent_token_owner_scope";
let ACTIVE_AGENT_SCOPE = "anonymous";
const HELP_VIDEO_ID = "82eOJs7aTCE";
const HELP_VIDEO_EMBED_MODAL_URL = `https://www.youtube.com/embed/${HELP_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=https%3A%2F%2Fia.fullmargin.net`;
const VIDEO_WARMUP_LINKS = [
  { rel: "preconnect", href: "https://www.youtube.com" },
  { rel: "preconnect", href: "https://www.youtube-nocookie.com" },
  { rel: "preconnect", href: "https://i.ytimg.com" },
  { rel: "preconnect", href: "https://www.google.com" },
  { rel: "dns-prefetch", href: "https://www.youtube.com" },
  { rel: "dns-prefetch", href: "https://i.ytimg.com" },
];
const PLATFORM_URL = "https://ia.fullmargin.net";
const DEFAULT_MAX_AGENT_TOKENS = 5;

/* ---------------- Libellés de présentation (namespace « agentia ») ---------------- */
const AGENTIA_FR = {
  title: "Agent IA MT5",
  subtitle:
    "Connectez votre compte MetaTrader 5 à FullMetrix pour synchroniser vos données et utiliser les outils connectés.",
  actions: { download: "Télécharger l'Agent", tutorial: "Voir le tutoriel", refresh: "Actualiser" },
  tiles: {
    agent: {
      label: "État de l'agent",
      ok: "Agent connecté",
      okHint: "Vos données sont synchronisées.",
      incomplete: "Configuration incomplète",
      incompleteHint: "Terminez les étapes ci-dessous.",
    },
    account: { label: "Compte MT5", detected: "Compte détecté" },
    server: { label: "Serveur", connected: "Connecté", known: "Hors ligne" },
    sync: { label: "Dernière synchronisation", never: "Jamais synchronisé", waiting: "En attente de connexion" },
  },
  install: {
    title: "Installation rapide",
    subtitle: "Suivez ces 4 étapes pour connecter votre compte MT5 à FullMetrix.",
    detailedGuide: "Voir le guide détaillé",
    prepare: {
      title: "Préparer MetaTrader 5",
      // Trois sous-étapes numérotées au lieu de trois puces : les puces
      // disaient QUOI faire sans dire OÙ, et les intitulés repris ici sont
      // ceux réellement affichés par MT5 — sinon l'utilisateur cherche des
      // cases qui ne portent pas ce nom.
      steps: [
        {
          title: "Accéder aux options",
          text: "Dans MT5 : menu Outils → Options → onglet « Conseillers Experts » (affiché « Expert Advisors » sur certaines versions).",
        },
        {
          title: "Activer les autorisations",
          text: "Cochez « Autoriser le trading algorithmique », « Autoriser les importations DLL » et « Autoriser WebRequest pour les URL listées ».",
        },
        {
          title: "Autoriser l’adresse",
          text: "Copiez l’adresse ci-dessous, collez-la dans la liste WebRequest, puis validez avec OK.",
        },
      ],
    },
    download: {
      title: "Télécharger et installer l'Agent",
      text: "Téléchargez l'Agent compatible avec votre terminal MT5 et installez-le dans le dossier Experts.",
      cta: "Télécharger l'Agent",
    },
    token: {
      title: "Créer un token",
      text: "Créez un token unique pour ce compte MT5 et collez-le dans les paramètres de l'Agent. Un token ne peut être lié qu’à un seul compte.",
      cta: "Créer un token",
    },
    verify: {
      title: "Vérifier la connexion",
      text: "Assurez-vous que l'Agent est actif et que les données sont bien synchronisées.",
    },
  },
  tokens: {
    title: "Tokens de connexion",
    subtitle: "Utilisez un token différent pour chaque compte MT5. Le premier compte connecté verrouille définitivement le token sur son serveur et son identifiant.",
    create: "Créer un nouveau token",
    activeLabel: "Token de cet appareil",
    current: "Actuel",
    unnamed: "Token sans nom",
    columns: { name: "Nom du token" },
    binding: {
      bound: "Lié au compte",
      unbound: "En attente de liaison",
      boundHint: "Ce token est verrouillé sur ce compte. Pour un autre compte, générez un nouveau token.",
      unboundHint: "Le premier compte MT5 qui utilise ce token deviendra son compte définitif.",
      createRule: "1 token = 1 compte MT5. Dès la première connexion de l’Agent, ce token sera verrouillé sur le serveur et l’identifiant de ce compte.",
    },
    actions: {
      rename: "Modifier le nom",
      delete: "Supprimer définitivement",
    },
    rename: {
      title: "Modifier le nom du token",
      subtitle: "Donnez un nom clair à ce token pour identifier facilement le compte ou le terminal associé.",
      nameLabel: "Nouveau nom",
      namePlaceholder: "Ex. Compte réel IC Markets",
      nameHint: "Le nom peut contenir jusqu’à 120 caractères.",
      cancel: "Annuler",
      saving: "Enregistrement…",
      save: "Enregistrer le nom",
      required: "Saisissez un nom pour ce token.",
      success: "Le nom du token a été modifié.",
      failed: "Impossible de modifier le nom : {{error}}",
    },
    delete: {
      title: "Supprimer définitivement ce token ?",
      subtitle: "Le token « {{name}} » sera supprimé du serveur.",
      warning: "Cette action est irréversible. Tout Agent MT5 qui utilise ce token sera déconnecté et devra recevoir un nouveau token.",
      cancel: "Annuler",
      deleting: "Suppression…",
      confirm: "Supprimer définitivement",
      success: "Le token a été supprimé définitivement.",
      failed: "Impossible de supprimer le token : {{error}}",
    },
    limit: {
      summary: "{{count}} / {{max}} tokens utilisés",
      available: "Places disponibles : {{count}}",
      reached: "Limite atteinte : vous pouvez créer jusqu’à {{max}} tokens. Supprimez un token pour en créer un nouveau.",
      createDisabled: "Limite de {{max}} tokens atteinte",
    },
    securityNote: "Vos tokens sont chiffrés et sécurisés. Seuls vous et votre Agent pouvez y accéder.",
  },
  aside: {
    video: {
      title: "Tutoriel vidéo",
      text: "Apprenez à installer et configurer votre Agent IA MT5 en quelques minutes.",
      badge: "Installation Agent IA MT5",
      cta: "Voir le tutoriel complet",
    },
    tip: {
      title: "Bon à savoir",
      text: "Un token ne peut servir qu’à un seul compte MT5. Pour connecter un autre compte Deriv ou broker, générez obligatoirement un nouveau token.",
    },
    support: {
      title: "Problème de connexion ?",
      text: "Notre équipe est là pour vous aider à résoudre tout problème rapidement.",
      cta: "Contacter le support WhatsApp",
      note: "Réponse rapide garantie",
    },
  },
};

const AGENTIA_EN = {
  title: "MT5 AI Agent",
  subtitle: "Connect your MetaTrader 5 account to FullMetrix to sync your data and use the connected tools.",
  actions: { download: "Download the Agent", tutorial: "Watch the tutorial", refresh: "Refresh" },
  tiles: {
    agent: {
      label: "Agent status",
      ok: "Agent connected",
      okHint: "Your data is syncing.",
      incomplete: "Setup incomplete",
      incompleteHint: "Complete the steps below.",
    },
    account: { label: "MT5 account", detected: "Account detected" },
    server: { label: "Server", connected: "Connected", known: "Offline" },
    sync: { label: "Last sync", never: "Never synced", waiting: "Waiting for connection" },
  },
  install: {
    title: "Quick setup",
    subtitle: "Follow these 4 steps to connect your MT5 account to FullMetrix.",
    detailedGuide: "Open the detailed guide",
    prepare: {
      title: "Prepare MetaTrader 5",
      steps: [
        {
          title: "Open the options",
          text: "In MT5: Tools menu → Options → “Expert Advisors” tab.",
        },
        {
          title: "Enable the permissions",
          text: "Tick “Allow algorithmic trading”, “Allow DLL imports” and “Allow WebRequest for listed URL”.",
        },
        {
          title: "Allow the address",
          text: "Copy the address below, paste it into the WebRequest list, then confirm with OK.",
        },
      ],
    },
    download: {
      title: "Download and install the Agent",
      text: "Download the Agent for your MT5 terminal and install it in the Experts folder.",
      cta: "Download the Agent",
    },
    token: {
      title: "Create a token",
      text: "Create a unique token for this MT5 account and paste it into the Agent inputs. One token can only be bound to one account.",
      cta: "Create a token",
    },
    verify: { title: "Check the connection", text: "Make sure the Agent is running and the data is syncing." },
  },
  tokens: {
    title: "Connection tokens",
    subtitle: "Use a different token for each MT5 account. The first connected account permanently locks the token to its server and login.",
    create: "Create a new token",
    activeLabel: "Token on this device",
    current: "Current",
    unnamed: "Unnamed token",
    columns: { name: "Token name" },
    binding: {
      bound: "Bound account",
      unbound: "Waiting for binding",
      boundHint: "This token is locked to this account. Generate a new token for any other account.",
      unboundHint: "The first MT5 account using this token will become its permanent account.",
      createRule: "1 token = 1 MT5 account. On the Agent’s first connection, this token will be permanently locked to that account’s server and login.",
    },
    actions: {
      rename: "Rename token",
      delete: "Delete permanently",
    },
    rename: {
      title: "Rename token",
      subtitle: "Give this token a clear name so you can easily identify its account or terminal.",
      nameLabel: "New name",
      namePlaceholder: "E.g. IC Markets live account",
      nameHint: "The name can contain up to 120 characters.",
      cancel: "Cancel",
      saving: "Saving…",
      save: "Save name",
      required: "Enter a name for this token.",
      success: "The token name was updated.",
      failed: "Unable to rename the token: {{error}}",
    },
    delete: {
      title: "Delete this token permanently?",
      subtitle: "The token “{{name}}” will be removed from the server.",
      warning: "This action cannot be undone. Any MT5 Agent using this token will be disconnected and will need a new token.",
      cancel: "Cancel",
      deleting: "Deleting…",
      confirm: "Delete permanently",
      success: "The token was permanently deleted.",
      failed: "Unable to delete the token: {{error}}",
    },
    limit: {
      summary: "{{count}} / {{max}} tokens used",
      available: "Available slots: {{count}}",
      reached: "Limit reached: you can create up to {{max}} tokens. Delete a token before creating a new one.",
      createDisabled: "{{max}}-token limit reached",
    },
    securityNote: "Your tokens are encrypted and secured. Only you and your Agent can access them.",
  },
  aside: {
    video: {
      title: "Video tutorial",
      text: "Learn how to install and configure your MT5 AI Agent in a few minutes.",
      badge: "MT5 AI Agent setup",
      cta: "Watch the full tutorial",
    },
    tip: {
      title: "Good to know",
      text: "One token can only be used by one MT5 account. Generate a new token for every other Deriv or broker account.",
    },
    support: {
      title: "Connection problem?",
      text: "Our team is here to help you solve any issue quickly.",
      cta: "Contact WhatsApp support",
      note: "Fast reply guaranteed",
    },
  },
};

i18n.addResourceBundle("fr", "agentia", AGENTIA_FR, true, true);
i18n.addResourceBundle("en", "agentia", AGENTIA_EN, true, true);

const storage = {
  get(key) {
    try {
      if (typeof window === "undefined") return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(key, value);
    } catch {}
  },
  remove(key) {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.removeItem(key);
    } catch {}
  },
};


function safeJsonParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function getStorageValue(key) {
  try {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function getTokenCandidates() {
  const keys = [
    "token",
    "access_token",
    "accessToken",
    "auth_token",
    "jwt",
    "jwt_token",
    "fm_token",
    "authToken",
    "AUTH_TOKEN",
  ];

  const out = [];
  for (const key of keys) {
    const value = getStorageValue(key);
    if (value) out.push(value);
  }

  try {
    const rawUser = getStorageValue("user");
    const parsed = safeJsonParse(rawUser, null);
    const nested = parsed?.token || parsed?.access_token || parsed?.jwt || "";
    if (nested) out.push(nested);
  } catch {}

  return Array.from(new Set(out.filter(Boolean)));
}

function decodeJwtPayload(token = "") {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      Array.from(window.atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getUserIdentityValue(payload = {}) {
  const source = payload?.user || payload?.profile || payload?.data || payload || {};
  const candidates = [
    source.id,
    source.user_id,
    source.userId,
    source.fullmargin_id,
    source.fullmarginId,
    source.sub,
    source.email,
    source.username,
  ];

  const found = candidates.find((value) => value !== null && value !== undefined && String(value).trim());
  return found ? String(found).trim() : "";
}

function hashString(value = "") {
  const str = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function makeUserScope(identity = "") {
  const raw = String(identity || "").trim().toLowerCase();
  return raw ? `user_${hashString(raw)}` : "anonymous";
}

function scopedStorageKey(baseKey, scope = ACTIVE_AGENT_SCOPE) {
  const safeScope = String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${baseKey}:${safeScope || "anonymous"}`;
}

function setActiveAgentScope(scope = "anonymous") {
  const safeScope = String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_");
  ACTIVE_AGENT_SCOPE = safeScope || "anonymous";
  try { window.sessionStorage.setItem("fm:agent:active_scope", ACTIVE_AGENT_SCOPE); } catch {}
  return ACTIVE_AGENT_SCOPE;
}

function resolveStoredSessionProfile() {
  try {
    const storageKeys = [
      "user",
      "auth_user",
      "currentUser",
      "fm_user",
      "fullmetrix_user",
      "fullmargin_user",
      "profile",
      "sso_user",
      "user_data",
      "authData",
    ];

    for (const key of storageKeys) {
      const raw = getStorageValue(key);
      if (!raw) continue;
      const parsed = safeJsonParse(raw, raw);
      if (getUserIdentityValue(parsed)) return parsed;
    }

    for (const token of getTokenCandidates()) {
      const payload = decodeJwtPayload(token);
      if (getUserIdentityValue(payload)) return payload;
    }
  } catch {}
  return null;
}

function resolveStoredAgentScope() {
  const profile = resolveStoredSessionProfile();
  const identity = getUserIdentityValue(profile);
  if (identity) return makeUserScope(identity);

  // Fallback utile quand l'app est authentifiée par cookie ou token opaque
  // mais que le profil utilisateur n'est pas encore dans localStorage.
  const authToken = getTokenCandidates()[0] || "";
  return makeUserScope(authToken);
}

function readAgentTokenForScope(scope = ACTIVE_AGENT_SCOPE) {
  const safeScope = String(scope || "anonymous");
  if (!safeScope || safeScope === "anonymous") return "";

  const scoped = storage.get(scopedStorageKey(LS_TOKEN, safeScope));
  if (scoped) return scoped;

  const owner = storage.get(LS_TOKEN_OWNER) || "";
  const legacy = storage.get(LS_TOKEN) || "";
  if (legacy && owner && owner === safeScope) return legacy;

  return "";
}

function saveAgentTokenForScope(token, scope = ACTIVE_AGENT_SCOPE) {
  const value = String(token || "").trim();
  const safeScope = String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_") || "anonymous";
  if (!value) return;

  // Pont de session lu par ChatPage immédiatement après création/activation.
  // Même si le scope est encore anonymous, le chat peut utiliser le token
  // pendant la session courante, puis récupérer la source fiable via /api/agent/tokens.
  try {
    window.sessionStorage.setItem(scopedStorageKey(LS_TOKEN, safeScope), value);
    window.sessionStorage.setItem("fm:agent_token:current", value);
    window.sessionStorage.setItem("fm:agent_token:current_scope", safeScope);
  } catch {}

  // Persistance durable uniquement quand le compte est clairement identifié.
  if (safeScope !== "anonymous") {
    storage.set(scopedStorageKey(LS_TOKEN, safeScope), value);

    // Compatibilité avec les anciennes pages, mais avec propriétaire explicite.
    storage.set(LS_TOKEN, value);
    storage.set(LS_TOKEN_OWNER, safeScope);
  }

  try {
    window.dispatchEvent(new CustomEvent("fullmetrix:agent-token-changed", {
      detail: { scope: safeScope, token: value },
    }));
  } catch {}
}

function removeAgentTokenForScope(scope = ACTIVE_AGENT_SCOPE) {
  const safeScope = String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_") || "anonymous";
  storage.remove(scopedStorageKey(LS_TOKEN, safeScope));

  try {
    window.sessionStorage.removeItem(scopedStorageKey(LS_TOKEN, safeScope));
    const bridgeScope = window.sessionStorage.getItem("fm:agent_token:current_scope") || "";
    if (bridgeScope === safeScope) {
      window.sessionStorage.removeItem("fm:agent_token:current");
      window.sessionStorage.removeItem("fm:agent_token:current_scope");
    }
  } catch {}

  const owner = storage.get(LS_TOKEN_OWNER) || "";
  if (owner === safeScope) {
    storage.remove(LS_TOKEN);
    storage.remove(LS_TOKEN_OWNER);
  }

  try {
    window.dispatchEvent(new CustomEvent("fullmetrix:agent-token-changed", {
      detail: { scope: safeScope, token: "" },
    }));
  } catch {}
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toDate(value) {
  if (value == null) return null;
  if (typeof value === "number") {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  const d = new Date(value);
  return Number.isNaN(+d) ? null : d;
}

function fmtTS(value) {
  const d = toDate(value);
  return d ? d.toLocaleString() : "—";
}

function fmtCompactTS(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtAgo(value, t) {
  const d = toDate(value);
  if (!d) return t("agentIA.time.never");

  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);

  const units = [
    { ms: 1000 * 60 * 60 * 24 * 365, key: "year" },
    { ms: 1000 * 60 * 60 * 24 * 30, key: "month" },
    { ms: 1000 * 60 * 60 * 24, key: "day" },
    { ms: 1000 * 60 * 60, key: "hour" },
    { ms: 1000 * 60, key: "minute" },
    { ms: 1000, key: "second" },
  ];

  const picked = units.find((u) => abs >= u.ms) || units[units.length - 1];
  const count = Math.max(1, Math.round(abs / picked.ms));

  const unit = t(`agentIA.time.units.${picked.key}`, { count });
  return t("agentIA.time.ago", { unit });
}

function maskToken(value) {
  const v = String(value || "");
  if (!v) return "—";
  if (v.length <= 18) return v;
  return `${v.slice(0, 8)}••••••${v.slice(-8)}`;
}

const pageBg = cn(
  "relative min-h-[100dvh] overflow-x-hidden bg-[#f6f7fb] text-slate-900",
  "dark:bg-[#060914] dark:text-white"
);

const shellCard = cn(
  "relative overflow-hidden rounded-[28px] border border-slate-200/80 bg-white/95 ",
  "dark:border-white/10 dark:bg-[#0d1322]/90 dark:"
);

const softCard = cn(
  "rounded-[20px] border border-slate-200/75 bg-slate-50/95",
  "dark:border-white/10 dark:bg-white/[0.04]"
);

// Champ de saisie : haut (48px), coins de champ et non de pilule (rounded-2xl
// sur 44px de haut donnait une pilule, qui se lit comme une puce), fond en aplat
// pour qu'on voie où écrire, bordure assumée.
const inputClass = cn(
  "h-12 w-full rounded-xl border px-4 text-sm outline-none transition",
  "border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400",
  "focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10",
  "dark:border-white/12 dark:bg-[#0d1424] dark:text-white dark:placeholder:text-white/30",
  "dark:focus:border-violet-400 dark:focus:bg-[#11192d]"
);

const btnBase =
  "inline-flex items-center justify-center gap-1.5 rounded-xl text-[12px] font-semibold transition duration-150 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60";

const btnPrimary = cn(
  btnBase,
  "h-9 px-3 text-white",
  "bg-violet-600",
  " hover:brightness-110"
);

const btnSoft = cn(
  btnBase,
  "h-9 px-3 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50",
  "dark:border-white/10 dark:bg-white/[0.055] dark:text-white dark:hover:bg-white/[0.09]"
);

const btnDanger = cn(
  btnBase,
  "h-9 px-3 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100",
  "dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:bg-rose-500/15"
);

// Sans la taille : `cn` ici est un simple join (pas de tailwind-merge), donc un
// h-9 embarqué dans la classe de base ne se laisse pas surcharger — c'est l'ordre
// du CSS généré qui tranche, pas celui des arguments. Qui veut une autre taille
// part de iconBtnBase.
const iconBtnBase = cn(
  "inline-flex items-center justify-center rounded-xl transition",
  "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
  "dark:border-white/10 dark:bg-white/[0.055] dark:text-white dark:hover:bg-white/[0.09]"
);
const iconBtn = cn(iconBtnBase, "h-9 w-9");

function warmVideoConnection() {
  if (typeof document === "undefined") return;

  VIDEO_WARMUP_LINKS.forEach(({ rel, href }) => {
    const id = `fm-video-${rel}-${href.replace(/[^a-z0-9]/gi, "-")}`;
    if (document.getElementById(id)) return;

    const link = document.createElement("link");
    link.id = id;
    link.rel = rel;
    link.href = href;
    if (rel === "preconnect") link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  });
}

function getVideoViewport(root) {
  if (typeof window === "undefined") return { left: 0, width: "100vw" };

  const viewportWidth = window.innerWidth || 0;
  if (!root || viewportWidth < 768) return { left: 0, width: "100vw" };

  const rect = root.getBoundingClientRect();
  const left = Math.max(0, Math.round(rect.left));
  const right = Math.min(viewportWidth, Math.round(rect.right));
  const width = Math.max(320, right - left || viewportWidth - left);

  return { left, width: `${width}px` };
}

function sendYoutubeCommand(iframe, command) {
  try {
    const target = iframe?.contentWindow;
    if (!target) return;

    target.postMessage(
      JSON.stringify({
        event: "command",
        func: command,
        args: [],
      }),
      "*"
    );
  } catch {}
}

function getTokenRowKey(item = {}) {
  return String(item?.id || item?.token_id || item?.uuid || item?.public_id || item?.token || "");
}

function BackgroundGlow() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-1/2 top-[-180px] h-[340px] w-[340px] -translate-x-1/2 rounded-full bg-violet-500/10 blur-3xl dark:bg-violet-500/14" />
      <div className="absolute left-[-80px] top-[26%] h-[220px] w-[220px] rounded-full bg-fuchsia-500/6 blur-3xl dark:bg-fuchsia-500/10" />
      <div className="absolute bottom-[-90px] right-[12%] h-[260px] w-[260px] rounded-full bg-sky-400/7 blur-3xl dark:bg-sky-500/10" />
    </div>
  );
}

function AccentTopLine() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/70 to-transparent dark:via-violet-400/35"
    />
  );
}

// En-tête de carte d'étape. Même règle que SectionTitle : la description sort de
// la colonne de l'icône pour courir sur toute la largeur, alignée à gauche.
// Tuile d'état de l'en-tête : libellé, valeur, puce ou précision dessous.
const TILE_TONES = {
  violet: "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
  emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  amber: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
  sky: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300",
};

function StatusTile({ icon: Icon, tone = "violet", label, value, hint, chip, chipTone = "slate" }) {
  return (
    <div className={cn(shellCard, "p-4")}>
      <div className="flex items-center gap-3">
        <span className={cn("inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", TILE_TONES[tone])}>
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-white/42">
            {label}
          </div>
          <div
            className={cn(
              "truncate text-[15px] font-semibold",
              tone === "amber"
                ? "text-amber-600 dark:text-amber-300"
                : tone === "emerald"
                ? "text-emerald-600 dark:text-emerald-300"
                : "text-slate-900 dark:text-white"
            )}
          >
            {value}
          </div>
        </div>
      </div>

      {chip ? (
        <span
          className={cn(
            "mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold",
            chipTone === "emerald"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300"
              : "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-white/60"
          )}
        >
          {chip}
        </span>
      ) : null}

      {hint ? <p className="mt-2 text-[12px] text-slate-500 dark:text-white/50">{hint}</p> : null}
    </div>
  );
}

// Étape de l'installation rapide : numéro + icône en ligne, contenu dessous.
function InstallStep({ index, icon: Icon, title, children }) {
  return (
    <div className={cn(softCard, "flex flex-col p-4")}>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[11px] font-bold text-white">
          {index}
        </span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
          <Icon size={16} />
        </span>
      </div>

      <h3 className="mt-3 text-[13.5px] font-semibold text-slate-900 dark:text-white">{title}</h3>
      {/* gap-3 : garde l'écart avec le bouton même quand il est poussé en bas. */}
      <div className="mt-2 flex flex-1 flex-col gap-3">{children}</div>
    </div>
  );
}

// Le sous-titre est hors de la colonne de l'icône : sinon il démarre 56px plus à
// droite (icône + gap) et perd d'autant de largeur. Il court donc sur toute la
// largeur, aligné à gauche avec le reste de la carte.
function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-700 ring-1 ring-violet-200/70 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20">
          <Icon size={18} />
        </span>
        <h2 className="min-w-0 text-base font-semibold text-slate-950 dark:text-white">{title}</h2>
      </div>
      {subtitle ? (
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-200">{subtitle}</p>
      ) : null}
    </div>
  );
}

function TinyInfo({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-slate-200/70 bg-slate-50 px-3 py-2 text-[12px] text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/55">
      <Icon size={13} />
      <span className="truncate">
        {label}: <span className="font-medium text-slate-800 dark:text-white/80">{value}</span>
      </span>
    </div>
  );
}

function StatusBadge({ active, children }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold",
        active
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
          : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200"
      )}
    >
      {active ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
      {children}
    </span>
  );
}

// Numéro + titre sur une ligne, texte dessous en pleine largeur — même règle que
// StepHeader / SectionTitle.
function EmptyState({ children }) {
  return (
    <div className="rounded-[22px] border border-slate-200/80 bg-white px-4 py-4 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.045] dark:text-white/62">
      {children}
    </div>
  );
}

function CompactActionButton({ title, tone = "soft", busy = false, disabled = false, onClick, children }) {
  const isDisabled = disabled || busy;
  const toneClass =
    tone === "danger"
      ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:bg-rose-500/15"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.055] dark:text-white dark:hover:bg-white/[0.09]";

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-busy={busy ? "true" : "false"}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-xl border transition duration-150 active:scale-95",
        "focus:outline-none focus:ring-2 focus:ring-violet-500/30",
        isDisabled ? "cursor-wait opacity-60" : "cursor-pointer",
        toneClass
      )}
    >
      {busy ? <RefreshCw size={13} className="animate-spin" /> : children}
    </button>
  );
}

function GlassToast({ type = "success", message, onClose }) {
  const { t } = useTranslation();
  const success = type === "success";

  return (
    <div
      className={cn(
        "pointer-events-auto relative overflow-hidden rounded-[22px] border px-4 py-3.5",
        "",
        success
          ? "border-emerald-200 bg-white text-slate-900 dark:border-emerald-400/25 dark:bg-[#0f1a24] dark:text-white"
          : "border-rose-200 bg-white text-slate-900 dark:border-rose-400/25 dark:bg-[#1a1220] dark:text-white"
      )}
    >
      <div
        aria-hidden
        className={cn(
          "absolute inset-x-0 top-0 h-px",
          success
            ? "bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent"
            : "bg-gradient-to-r from-transparent via-rose-400/70 to-transparent"
        )}
      />
      <div className="flex items-start gap-3 pr-9">
        <span
          className={cn(
            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border",
            success
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/15 dark:text-emerald-300"
              : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/25 dark:bg-rose-500/15 dark:text-rose-300"
          )}
        >
          {success ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
        </span>

        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-white/45">
            {success ? t("agentIA.toast.info") : t("agentIA.toast.error")}
          </div>
          <p className="mt-1 text-[13px] font-medium leading-6 text-slate-700 dark:text-white/82">
            {message}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200/70 bg-white/70 text-slate-500 transition hover:text-slate-900 dark:border-white/10 dark:bg-white/10 dark:text-white/60 dark:hover:text-white"
        aria-label={t("agentIA.toast.closeNotification")}
        title={t("agentIA.toast.close")}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default function AgentIA() {
  const { t } = useTranslation();
  // tp = libellés de présentation de cette page (namespace « agentia »).
  const { t: tp } = useTranslation("agentia", { useSuspense: false });
  const [agentScope, setAgentScope] = useState(() => setActiveAgentScope(resolveStoredAgentScope()));
  // `agentToken` = jeton réellement utilisé (localStorage + appels réseau).
  // `tokenDraft` = contenu du champ de saisie. Tant que l'utilisateur tape, on ne
  // touche ni au stockage ni à l'API : chaque frappe déclenchait sinon un
  // /agent/register qui créait un jeton serveur par caractère intermédiaire.
  const [agentToken, setAgentToken] = useState(() => readAgentTokenForScope(ACTIVE_AGENT_SCOPE));
  const [tokenDraft, setTokenDraft] = useState(agentToken);
  const [showToken, setShowToken] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const [serverTokens, setServerTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [serverApiReady, setServerApiReady] = useState(false);
  const [tokenLimit, setTokenLimit] = useState(DEFAULT_MAX_AGENT_TOKENS);

  const [tokenLabel, setTokenLabel] = useState("");
  const [createTokenModalOpen, setCreateTokenModalOpen] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);

  const [editingToken, setEditingToken] = useState(null);
  const [editingTokenLabel, setEditingTokenLabel] = useState("");
  const [renamingToken, setRenamingToken] = useState(false);

  const [tokenToDelete, setTokenToDelete] = useState(null);
  const [deletingToken, setDeletingToken] = useState(false);

  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");

  const [historySearch, setHistorySearch] = useState("");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoViewport, setVideoViewport] = useState({ left: 0, width: "100vw" });
  const [tokenAction, setTokenAction] = useState({ key: "", action: "" });

  const pageRootRef = useRef(null);
  const videoIframeRef = useRef(null);
  const flashTimerRef = useRef(null);
  const guideRef = useRef(null);
  const agentScopeRef = useRef(agentScope);
  // Lu par fetchStatus / syncScope pour qu'ils gardent une identité stable :
  // sans ça leurs effets se réabonnaient à chaque changement de jeton.
  const agentTokenRef = useRef(agentToken);
  // Un /agent/register par jeton et par session : le polling (3 s) le rejouait
  // en boucle et ne récoltait que des 409 « déjà enregistré ».
  const registeredTokensRef = useRef(new Set());

  const hasToken = useMemo(() => !!agentToken.trim(), [agentToken]);
  const hasDraft = useMemo(() => !!tokenDraft.trim(), [tokenDraft]);
  const tokenCount = serverTokens.length;
  const remainingTokenSlots = Math.max(tokenLimit - tokenCount, 0);
  const tokenLimitReached = serverApiReady && tokenCount >= tokenLimit;

  useEffect(() => {
    warmVideoConnection();
  }, []);

  useEffect(() => {
    const activeScope = setActiveAgentScope(agentScope);
    agentScopeRef.current = activeScope;
  }, [agentScope]);

  // Le champ suit le jeton actif quand celui-ci change par une action (activer,
  // créer, supprimer, changement d'onglet) — jamais pendant la frappe.
  useEffect(() => {
    agentTokenRef.current = agentToken;
    setTokenDraft(agentToken);
  }, [agentToken]);

  useEffect(() => {
    const syncScope = () => {
      const nextScope = setActiveAgentScope(resolveStoredAgentScope());
      const nextToken = readAgentTokenForScope(nextScope);

      if (nextScope !== agentScopeRef.current) {
        agentScopeRef.current = nextScope;
        setAgentScope(nextScope);
        setAgentToken(nextToken);
        setStatus(null);
        setErr("");
        return;
      }

      if (nextToken && nextToken !== agentTokenRef.current) {
        setAgentToken(nextToken);
      }
    };

    syncScope();

    if (typeof window === "undefined") return undefined;

    window.addEventListener("focus", syncScope);
    window.addEventListener("storage", syncScope);
    window.addEventListener("fullmetrix:agent-token-changed", syncScope);
    document.addEventListener("visibilitychange", syncScope);

    return () => {
      window.removeEventListener("focus", syncScope);
      window.removeEventListener("storage", syncScope);
      window.removeEventListener("fullmetrix:agent-token-changed", syncScope);
      document.removeEventListener("visibilitychange", syncScope);
    };
  }, []);

  const account = status?.account ?? {};
  const brokerServer = String(
    account.server ?? account.trade_server ?? account.broker_server ?? account.broker ?? ""
  );
  const accountLogin = String(
    account.login ?? account.account ?? account.number ?? account.id ?? ""
  );

  const lastHeartbeat =
    status?.last_heartbeat ?? status?.lastHeartbeat ?? account?.last_heartbeat ?? null;

  const isConnected = useMemo(() => {
    const d = toDate(lastHeartbeat);
    if (!d) return false;
    return Date.now() - d.getTime() < 20000;
  }, [lastHeartbeat]);

  const flashMsg = useCallback((message) => {
    setFlash(message);
    if (typeof window !== "undefined") {
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => setFlash(""), 2200);
    }
  }, []);

  const clearFlash = useCallback(() => {
    if (typeof window !== "undefined") {
      window.clearTimeout(flashTimerRef.current);
    }
    setFlash("");
  }, []);

  const openVideoModal = useCallback(() => {
    warmVideoConnection();
    setVideoViewport(getVideoViewport(pageRootRef.current));
    setVideoModalOpen(true);

    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        sendYoutubeCommand(videoIframeRef.current, "playVideo");
      }, 120);
    }
  }, []);

  const closeVideoModal = useCallback(() => {
    sendYoutubeCommand(videoIframeRef.current, "pauseVideo");
    setVideoModalOpen(false);
  }, []);

  const syncVideoViewport = useCallback(() => {
    setVideoViewport(getVideoViewport(pageRootRef.current));
  }, []);

  useEffect(() => {
    if (!videoModalOpen || !videoReady) return;
    sendYoutubeCommand(videoIframeRef.current, "playVideo");
  }, [videoModalOpen, videoReady]);

  const openCreateTokenModal = useCallback(() => {
    setErr("");

    if (tokenLimitReached) {
      setErr(tp("tokens.limit.reached", { max: tokenLimit }));
      return;
    }

    setTokenLabel("");
    setCreateTokenModalOpen(true);
  }, [tokenLimitReached, tokenLimit, tp]);

  const closeCreateTokenModal = useCallback(() => {
    if (creatingToken) return;
    setCreateTokenModalOpen(false);
    setTokenLabel("");
  }, [creatingToken]);

  const openRenameTokenModal = useCallback((item) => {
    setErr("");
    setEditingToken(item || null);
    setEditingTokenLabel(String(item?.label || ""));
  }, []);

  const closeRenameTokenModal = useCallback(() => {
    if (renamingToken) return;
    setEditingToken(null);
    setEditingTokenLabel("");
  }, [renamingToken]);

  const openDeleteTokenModal = useCallback((item) => {
    setErr("");
    setTokenToDelete(item || null);
  }, []);

  const closeDeleteTokenModal = useCallback(() => {
    if (deletingToken) return;
    setTokenToDelete(null);
  }, [deletingToken]);

  const copyText = useCallback(
    async (value, successMessage = t("agentIA.messages.copied")) => {
      const text = String(value || "").trim();
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        flashMsg(successMessage);
      } catch {
        setErr(t("agentIA.messages.copyFailed"));
      }
    },
    [flashMsg]
  );

  const fetchStatus = useCallback(
    async (tokenOverride) => {
      // Jamais la valeur du champ : uniquement le jeton commité, sinon chaque
      // frappe partait au serveur (register + status) et créait un agent.
      const token = String(tokenOverride ?? agentTokenRef.current).trim();

      if (!token) {
        setStatus(null);
        return;
      }

      setLoading(true);
      setErr("");

      try {
        if (!registeredTokensRef.current.has(token)) {
          registeredTokensRef.current.add(token);
          await apiAgentRegisterExisting(token);
        }

        const data = await apiAgentStatus(token);
        setStatus(data || null);
      } catch (e) {
        setStatus(null);
        setErr(t("agentIA.messages.statusUnavailable", { error: e?.message || e }));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadServerTokens = useCallback(async () => {
    setTokensLoading(true);

    try {
      const data = await apiAgentListTokens();
      const items = Array.isArray(data?.tokens) ? data.tokens : [];
      const apiLimit = Number(data?.max_tokens);

      setServerTokens(items);
      setTokenLimit(
        Number.isFinite(apiLimit) && apiLimit > 0
          ? Math.floor(apiLimit)
          : DEFAULT_MAX_AGENT_TOKENS
      );
      setServerApiReady(true);

      const scope = agentScopeRef.current || ACTIVE_AGENT_SCOPE;
      const scopedToken = readAgentTokenForScope(scope);
      if (!scopedToken) {
        const preferred = items.find((item) => item?.active && item?.token)?.token || "";
        if (preferred) {
          saveAgentTokenForScope(preferred, scope);
          setAgentToken(preferred);
        }
      }
    } catch (e) {
      setServerApiReady(false);
      if (e?.status && ![401, 403, 404].includes(e.status)) {
        setErr(t("agentIA.messages.tokensLoadFailed", { error: e?.message || e }));
      }
    } finally {
      setTokensLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    loadServerTokens();
  }, [fetchStatus, loadServerTokens]);

  useEffect(() => {
    if (!hasToken) return undefined;

    const id = window.setInterval(() => {
      fetchStatus();
    }, 3000);

    return () => window.clearInterval(id);
  }, [hasToken, fetchStatus]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        window.clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;

    if (videoModalOpen || createTokenModalOpen || editingToken || tokenToDelete) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [videoModalOpen, createTokenModalOpen, editingToken, tokenToDelete]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (videoModalOpen) setVideoModalOpen(false);
      if (createTokenModalOpen && !creatingToken) {
        setCreateTokenModalOpen(false);
        setTokenLabel("");
      }
      if (editingToken && !renamingToken) {
        setEditingToken(null);
        setEditingTokenLabel("");
      }
      if (tokenToDelete && !deletingToken) {
        setTokenToDelete(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    videoModalOpen,
    createTokenModalOpen,
    creatingToken,
    editingToken,
    renamingToken,
    tokenToDelete,
    deletingToken,
  ]);

  useEffect(() => {
    if (!videoModalOpen || typeof window === "undefined") return undefined;

    syncVideoViewport();

    let frameId = null;
    const handleResize = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(syncVideoViewport);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    let observer = null;
    if (typeof ResizeObserver !== "undefined" && pageRootRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(pageRootRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (frameId) window.cancelAnimationFrame(frameId);
      if (observer) observer.disconnect();
    };
  }, [videoModalOpen, syncVideoViewport]);


  const refreshAll = useCallback(async () => {
    await Promise.all([fetchStatus(), loadServerTokens()]);
  }, [fetchStatus, loadServerTokens]);

  const setActiveTokenLocal = useCallback(
    async (token, message = t("agentIA.messages.keyActivated")) => {
      const value = String(token || "").trim();
      if (!value) return;

      saveAgentTokenForScope(value, agentScopeRef.current);
      setAgentToken(value);

      registeredTokensRef.current.add(value);
      await apiAgentRegisterExisting(value);
      await fetchStatus(value);
      await loadServerTokens();

      flashMsg(message);
    },
    [fetchStatus, loadServerTokens, flashMsg]
  );

  // Seul point d'entrée manuel : c'est ici que la saisie devient le jeton actif.
  const saveTokenLocal = useCallback(async () => {
    const token = tokenDraft.trim();
    if (!token) {
      setErr(t("agentIA.messages.pasteBeforeSave"));
      return;
    }

    setErr("");

    try {
      saveAgentTokenForScope(token, agentScopeRef.current);
      setAgentToken(token);
      registeredTokensRef.current.add(token);
      await apiAgentRegisterExisting(token);
      await fetchStatus(token);
      await loadServerTokens();
      flashMsg(t("agentIA.messages.keySaved"));
    } catch (e) {
      setErr(t("agentIA.messages.keyUseFailed", { error: e?.message || e }));
    }
  }, [tokenDraft, fetchStatus, loadServerTokens, flashMsg]);

  const createServerToken = useCallback(
    async (customLabel) => {
      setErr("");

      if (tokenLimitReached) {
        setCreateTokenModalOpen(false);
        setErr(tp("tokens.limit.reached", { max: tokenLimit }));
        return;
      }

      setCreatingToken(true);

      try {
        const labelValue = String(customLabel ?? tokenLabel).trim();

        const data = await apiAgentCreateToken({
          label: labelValue,
          active: true,
        });

        const token = data?.token || data?.item?.token || "";
        setTokenLabel("");
        setCreateTokenModalOpen(false);
        await loadServerTokens();

        if (token) {
          await setActiveTokenLocal(token, t("agentIA.messages.newKeyCreated"));
        } else {
          flashMsg(t("agentIA.messages.keyCreated"));
        }
      } catch (e) {
        const errorCode = String(
          e?.code ||
          e?.data?.error ||
          e?.response?.data?.error ||
          e?.body?.error ||
          ""
        );

        if (errorCode === "token_limit_reached") {
          setCreateTokenModalOpen(false);
          await loadServerTokens();
          setErr(tp("tokens.limit.reached", { max: tokenLimit }));
          return;
        }

        if (e?.status === 401 || e?.status === 403 || e?.status === 404) {
          try {
            const response = await apiAgentRegister();
            const token = response?.agent_token;
            if (!token) throw new Error(t("agentIA.messages.createImpossible"));

            saveAgentTokenForScope(token, agentScopeRef.current);
            setAgentToken(token);
            setTokenLabel("");
            setCreateTokenModalOpen(false);

            await apiAgentRegisterExisting(token);
            await fetchStatus(token);

            flashMsg(t("agentIA.messages.keyCreated"));
            return;
          } catch (fallbackErr) {
            setErr(t("agentIA.messages.createFailed", { error: fallbackErr?.message || fallbackErr }));
            return;
          }
        }

        setErr(t("agentIA.messages.createFailed", { error: e?.message || e }));
      } finally {
        setCreatingToken(false);
      }
    },
    [
      tokenLabel,
      tokenLimitReached,
      tokenLimit,
      loadServerTokens,
      setActiveTokenLocal,
      flashMsg,
      fetchStatus,
      t,
      tp,
    ]
  );

  const renameServerToken = useCallback(async () => {
    const item = editingToken;
    const label = String(editingTokenLabel || "").trim();

    if (!item) return;
    if (!label) {
      setErr(tp("tokens.rename.required"));
      return;
    }

    const tokenId = item?.id || item?.token_id || item?.uuid || item?.public_id;
    if (!tokenId) {
      setErr(t("agentIA.messages.cannotModify"));
      return;
    }

    setRenamingToken(true);
    setErr("");

    try {
      await apiAgentRenameToken(tokenId, label);

      // Mise à jour optimiste pour que le nouveau nom apparaisse immédiatement.
      setServerTokens((current) =>
        current.map((tokenItem) =>
          getTokenRowKey(tokenItem) === getTokenRowKey(item)
            ? { ...tokenItem, label }
            : tokenItem
        )
      );

      setEditingToken(null);
      setEditingTokenLabel("");
      await loadServerTokens();
      flashMsg(tp("tokens.rename.success"));
    } catch (e) {
      setErr(tp("tokens.rename.failed", { error: e?.message || e }));
    } finally {
      setRenamingToken(false);
    }
  }, [editingToken, editingTokenLabel, loadServerTokens, flashMsg, t, tp]);

  const deleteServerToken = useCallback(async () => {
    const item = tokenToDelete;
    if (!item) return;

    const tokenId = item?.id || item?.token_id || item?.uuid || item?.public_id;
    if (!tokenId) {
      setErr(t("agentIA.messages.cannotModify"));
      return;
    }

    setDeletingToken(true);
    setErr("");

    try {
      await apiAgentDeleteToken(tokenId);

      const deletedTokenValue = String(item?.token || "");
      const wasCurrent = deletedTokenValue === String(agentToken || "");

      // Retrait immédiat de la liste, puis synchronisation avec le serveur.
      setServerTokens((current) =>
        current.filter((tokenItem) => getTokenRowKey(tokenItem) !== getTokenRowKey(item))
      );

      if (wasCurrent) {
        removeAgentTokenForScope(agentScopeRef.current);
        setAgentToken("");
        setStatus(null);
      }

      setTokenToDelete(null);
      await loadServerTokens();
      flashMsg(tp("tokens.delete.success"));
    } catch (e) {
      setErr(tp("tokens.delete.failed", { error: e?.message || e }));
    } finally {
      setDeletingToken(false);
    }
  }, [tokenToDelete, agentToken, loadServerTokens, flashMsg, t, tp]);

  const toggleTokenActive = useCallback(
    async (item, active) => {
      try {
        const tokenId = item?.id || item?.token_id || item?.uuid || item?.public_id;
        if (!tokenId) throw new Error(t("agentIA.messages.cannotModify"));

        await apiAgentSetTokenActive(tokenId, active);

        await loadServerTokens();

        if (!active && String(item.token || "") === String(agentToken || "")) {
          removeAgentTokenForScope(agentScopeRef.current);
          setAgentToken("");
          setStatus(null);
        }

        flashMsg(active ? t("agentIA.messages.keyReactivated") : t("agentIA.messages.keyDeactivated"));
      } catch (e) {
        setErr(t("agentIA.messages.modifyFailed", { error: e?.message || e }));
      }
    },
    [loadServerTokens, flashMsg, agentToken]
  );

  const disableTokenLocal = useCallback(() => {
    removeAgentTokenForScope(agentScopeRef.current);
    setAgentToken("");
    setTokenDraft("");
    setStatus(null);
    setErr("");
    flashMsg(t("agentIA.messages.keyNotUsedHere"));
  }, [flashMsg]);

  const copyToken = useCallback(async () => {
    if (!tokenDraft.trim()) return;
    await copyText(tokenDraft, t("agentIA.messages.keyCopied"));
  }, [tokenDraft, copyText]);

  const copyAnyToken = useCallback(
    async (value) => {
      await copyText(value, t("agentIA.messages.keyCopied"));
    },
    [copyText]
  );


  const runTokenAction = useCallback(async (item, action, task) => {
    const key = getTokenRowKey(item);
    if (!key) return;

    setTokenAction({ key, action });
    setErr("");

    try {
      await task();
    } catch (e) {
      setErr(t("agentIA.messages.actionFailed", { error: e?.message || e }));
    } finally {
      setTokenAction({ key: "", action: "" });
    }
  }, []);

  const handleUseToken = useCallback(
    (item) => {
      runTokenAction(item, "use", () => setActiveTokenLocal(item.token, t("agentIA.messages.keyActivated")));
    },
    [runTokenAction, setActiveTokenLocal]
  );

  const handleCopyAnyToken = useCallback(
    (item) => {
      runTokenAction(item, "copy", () => copyAnyToken(item.token));
    },
    [runTokenAction, copyAnyToken]
  );

  const handleToggleToken = useCallback(
    (item) => {
      runTokenAction(item, "toggle", () => toggleTokenActive(item, !item.active));
    },
    [runTokenAction, toggleTokenActive]
  );

  const handleRenameToken = useCallback(
    (item) => {
      if (tokenAction.key) return;
      openRenameTokenModal(item);
    },
    [tokenAction.key, openRenameTokenModal]
  );

  const handleDeleteToken = useCallback(
    (item) => {
      if (tokenAction.key) return;
      openDeleteTokenModal(item);
    },
    [tokenAction.key, openDeleteTokenModal]
  );

  const supportWhatsAppHref = useMemo(
    () => buildWhatsAppHref(SUPPORT_WHATSAPP, tp("aside.support.title")),
    [tp]
  );

  const handleDownload = useCallback(() => {
    const a = document.createElement("a");
    a.href = "/agents/FullMetrixAgent.ex5";
    a.download = "FullMetrixAgent.ex5";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const sortedTokens = useMemo(() => {
    return [...serverTokens].sort((a, b) => {
      const aCurrent = String(a.token || "") === String(agentToken || "");
      const bCurrent = String(b.token || "") === String(agentToken || "");

      if (aCurrent && !bCurrent) return -1;
      if (!aCurrent && bCurrent) return 1;

      if (!!a.active !== !!b.active) return a.active ? -1 : 1;

      const da = toDate(a.last_used_at || a.created_at)?.getTime() || 0;
      const db = toDate(b.last_used_at || b.created_at)?.getTime() || 0;
      return db - da;
    });
  }, [serverTokens, agentToken]);

  const filteredTokens = useMemo(() => {
    const q = historySearch.trim().toLowerCase();

    return sortedTokens.filter((item) => {
      if (!q) return true;

      const bag = [
        item.label || "",
        item.token || "",
        item.bound_server || item.bound_account?.server || "",
        item.bound_login || item.bound_account?.login || "",
        fmtTS(item.created_at),
        fmtTS(item.last_used_at),
      ]
        .join(" ")
        .toLowerCase();

      return bag.includes(q);
    });
  }, [sortedTokens, historySearch, agentToken]);

  return (
    <div ref={pageRootRef} className={pageBg}>
      <BackgroundGlow />

      <div className="relative mx-auto w-full max-w-[1540px] px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6 2xl:px-10">
        {/* EN-TÊTE — modèle commun à toutes les pages (PageHeader). */}
        <PageHeader
          icon={Cpu}
          title={tp("title")}
          subtitle={tp("subtitle")}
          actions={
            <>
              <button type="button" onClick={handleDownload} className={cn(btnPrimary, "h-10 px-4")}>
                <Download size={15} />
                {tp("actions.download")}
              </button>
              <button
                type="button"
                onPointerDown={warmVideoConnection}
                onMouseEnter={warmVideoConnection}
                onFocus={warmVideoConnection}
                onClick={openVideoModal}
                className={cn(btnSoft, "h-10 px-4")}
              >
                <Video size={15} className="text-violet-500" />
                {tp("actions.tutorial")}
              </button>
              <button
                type="button"
                onClick={refreshAll}
                disabled={loading || tokensLoading}
                className={cn(btnSoft, "h-10 px-4")}
              >
                <RefreshCw size={15} className={loading || tokensLoading ? "animate-spin" : ""} />
                {tp("actions.refresh")}
              </button>
            </>
          }
        />

        {/* 4 TUILES D'ÉTAT */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatusTile
            icon={isConnected ? CheckCircle2 : AlertTriangle}
            tone={isConnected ? "emerald" : "amber"}
            label={tp("tiles.agent.label")}
            value={isConnected ? tp("tiles.agent.ok") : tp("tiles.agent.incomplete")}
            hint={isConnected ? tp("tiles.agent.okHint") : tp("tiles.agent.incompleteHint")}
          />
          <StatusTile
            icon={ShieldCheck}
            tone="violet"
            label={tp("tiles.account.label")}
            value={accountLogin || "—"}
            chip={accountLogin ? tp("tiles.account.detected") : null}
          />
          <StatusTile
            icon={Globe}
            tone="violet"
            label={tp("tiles.server.label")}
            value={brokerServer || "—"}
            chip={brokerServer ? (isConnected ? tp("tiles.server.connected") : tp("tiles.server.known")) : null}
            chipTone={isConnected ? "emerald" : "slate"}
          />
          <StatusTile
            icon={RefreshCw}
            tone="sky"
            label={tp("tiles.sync.label")}
            value={lastHeartbeat ? fmtCompactTS(lastHeartbeat) : tp("tiles.sync.never")}
            hint={lastHeartbeat ? fmtAgo(lastHeartbeat, t) : tp("tiles.sync.waiting")}
          />
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
          {/* COLONNE PRINCIPALE */}
          <div className="min-w-0 space-y-4">
            {/* INSTALLATION RAPIDE */}
            <section className={cn(shellCard, "p-4 sm:p-5")} ref={guideRef}>
              <AccentTopLine />
              <SectionTitle icon={RefreshCw} title={tp("install.title")} subtitle={tp("install.subtitle")} />

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {/* 1 — Préparer MT5 */}
                <InstallStep index={1} icon={SlidersHorizontal} title={tp("install.prepare.title")}>
                  {/* Sous-étapes numérotées : le chemin dans MT5 doit se lire
                      dans l'ordre, une puce ronde ne porte pas cette idée. */}
                  <ol className="space-y-2 text-[12.5px] text-slate-600 dark:text-white/65">
                    {(tp("install.prepare.steps", { returnObjects: true }) || []).map(
                      (step, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="mt-[1px] inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-violet-500/12 text-[10px] font-semibold text-violet-600 dark:bg-violet-400/15 dark:text-violet-300">
                            {i + 1}
                          </span>
                          <span className="min-w-0">
                            <span className="font-semibold text-slate-800 dark:text-white/85">
                              {step.title}
                            </span>
                            <span className="mx-1 opacity-40">—</span>
                            <span className="leading-relaxed">{step.text}</span>
                          </span>
                        </li>
                      ),
                    )}
                  </ol>

                  <button
                    type="button"
                    onClick={() => copyText(PLATFORM_URL, t("agentIA.messages.addressCopied"))}
                    title={t("agentIA.guide.allow.copy")}
                    className="flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-left dark:border-white/15 dark:bg-[#11192d]"
                  >
                    <code className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-slate-700 dark:text-white/80">
                      {PLATFORM_URL}
                    </code>
                    <Copy size={13} className="shrink-0 text-slate-400" />
                  </button>
                </InstallStep>

                {/* 2 — Télécharger et installer */}
                <InstallStep index={2} icon={Download} title={tp("install.download.title")}>
                  <p className="text-[12.5px] leading-relaxed text-slate-600 dark:text-white/65">
                    {tp("install.download.text")}
                  </p>
                  {/* Plein comme l'étape 3 : télécharger l'Agent est une action
                      à faire, pas une option. En `btnSoft` elle se confondait
                      avec le bouton « Actualiser » de l'étape 4. */}
                  <button type="button" onClick={handleDownload} className={cn(btnPrimary, "mt-auto w-full")}>
                    <Download size={14} />
                    {tp("install.download.cta")}
                  </button>
                </InstallStep>

                {/* 3 — Créer un token */}
                <InstallStep index={3} icon={KeyRound} title={tp("install.token.title")}>
                  <p className="text-[12.5px] leading-relaxed text-slate-600 dark:text-white/65">
                    {tp("install.token.text")}
                  </p>
                  <button
                    type="button"
                    onClick={openCreateTokenModal}
                    disabled={tokenLimitReached}
                    title={tokenLimitReached ? tp("tokens.limit.createDisabled", { max: tokenLimit }) : undefined}
                    className={cn(btnPrimary, "mt-auto w-full")}
                  >
                    <Sparkles size={14} />
                    {tp("install.token.cta")}
                  </button>
                </InstallStep>

                {/* 4 — Vérifier */}
                <InstallStep index={4} icon={CheckCircle2} title={tp("install.verify.title")}>
                  <p className="text-[12.5px] leading-relaxed text-slate-600 dark:text-white/65">
                    {tp("install.verify.text")}
                  </p>
                  <button type="button" onClick={refreshAll} className={cn(btnSoft, "mt-auto w-full")}>
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                    {tp("actions.refresh")}
                  </button>
                </InstallStep>
              </div>

              <Link
                to={helpRoute("agent-ia-mt5")}
                className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-violet-600 hover:underline dark:text-violet-300"
              >
                {tp("install.detailedGuide")}
                <ArrowRight size={14} />
              </Link>
            </section>

            {/* TOKENS */}
            <section className={cn(shellCard, "p-4 sm:p-5")}>
              <AccentTopLine />

              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <SectionTitle icon={KeyRound} title={tp("tokens.title")} subtitle={tp("tokens.subtitle")} />

                <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                  <button
                    type="button"
                    onClick={openCreateTokenModal}
                    disabled={tokenLimitReached}
                    title={tokenLimitReached ? tp("tokens.limit.createDisabled", { max: tokenLimit }) : undefined}
                    className={cn(btnPrimary, "h-10 shrink-0 px-4")}
                  >
                    <Sparkles size={14} />
                    {tp("tokens.create")}
                  </button>

                  <div className="relative w-full sm:w-[260px]">
                    <Search
                      size={15}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-white/35"
                    />
                    <input
                      type="text"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder={t("agentIA.keysList.searchPlaceholder")}
                      className={cn(inputClass, "h-10 pl-9")}
                    />
                  </div>
                </div>
              </div>

              {/* Jeton utilisé sur cet appareil : saisie manuelle + actions. */}
              {/* Libellé, champ puis actions empilés : sur une colonne étroite
                  une seule ligne ne tient pas, les libellés se faisaient couper. */}
              <div className={cn(softCard, "mt-4 p-4 sm:p-5")}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-white/42">
                  {tp("tokens.activeLabel")}
                </div>

                <input
                  type={showToken ? "text" : "password"}
                  value={tokenDraft}
                  onChange={(e) => setTokenDraft(e.target.value)}
                  placeholder={t("agentIA.key.placeholder")}
                  className={cn(inputClass, "mt-3 h-11 w-full min-w-0 font-mono placeholder:font-sans")}
                />

                {/* Boutons à leur largeur naturelle : ils tiennent sur une ligne
                    et passent à la ligne seulement si la place manque. */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowToken((v) => !v)}
                    className={cn(iconBtnBase, "h-10 w-10 shrink-0")}
                    title={showToken ? t("agentIA.key.hide") : t("agentIA.key.show")}
                  >
                    {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button
                    type="button"
                    onClick={copyToken}
                    disabled={!hasDraft}
                    className={cn(iconBtnBase, "h-10 w-10 shrink-0 disabled:cursor-not-allowed disabled:opacity-50")}
                    title={t("agentIA.key.copy")}
                  >
                    <Copy size={15} />
                  </button>

                  <button type="button" onClick={saveTokenLocal} className={cn(btnSoft, "h-10 px-4")}>
                    <Save size={14} className="shrink-0" />
                    {t("agentIA.key.save")}
                  </button>

                  <button type="button" onClick={disableTokenLocal} className={cn(btnDanger, "h-10 px-4")}>
                    <PowerOff size={14} className="shrink-0" />
                    {t("agentIA.key.disconnect")}
                  </button>
                </div>
              </div>

              {!serverApiReady ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-medium text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100">
                  {t("agentIA.keysList.signInPrompt")}
                </div>
              ) : tokensLoading ? (
                <div className="mt-4 grid gap-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-[118px] animate-pulse rounded-2xl border border-slate-200/80 bg-white dark:border-white/10 dark:bg-white/[0.04]"
                    />
                  ))}
                </div>
              ) : filteredTokens.length ? (
                <div className="mt-4 grid gap-2.5">
                  {filteredTokens.map((item) => {
                    const isCurrent = String(item.token || "") === String(agentToken || "");
                    const rowKey = getTokenRowKey(item);
                    const isRowBusy = tokenAction.key === rowKey;
                    const isUseBusy = isRowBusy && tokenAction.action === "use";
                    const isCopyBusy = isRowBusy && tokenAction.action === "copy";
                    const isToggleBusy = isRowBusy && tokenAction.action === "toggle";
                    const isEditingThisToken =
                      getTokenRowKey(editingToken || {}) === rowKey;
                    const isDeletingThisToken =
                      getTokenRowKey(tokenToDelete || {}) === rowKey;

                    const boundServer =
                      item.bound_server || item.bound_account?.server || "—";
                    const boundLogin =
                      item.bound_login || item.bound_account?.login || "—";

                    return (
                      <article
                        key={String(item.id || item.token)}
                        className={cn(
                          "group relative overflow-hidden rounded-2xl border p-3 transition duration-200 sm:p-3.5",
                          isCurrent
                            ? "border-violet-300 bg-[linear-gradient(135deg,rgba(245,243,255,0.98),rgba(255,255,255,0.98)_48%,rgba(248,250,252,0.98))] ring-1 ring-violet-200/70 dark:border-violet-400/35 dark:bg-[linear-gradient(135deg,rgba(76,29,149,0.16),rgba(13,19,34,0.96)_48%,rgba(13,19,34,0.96))] dark:ring-violet-400/10"
                            : "border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/60 dark:border-white/10 dark:bg-white/[0.035] dark:hover:border-white/15 dark:hover:bg-white/[0.05]",
                          isRowBusy && "ring-2 ring-violet-500/15"
                        )}
                      >
                        {isCurrent ? (
                          <div
                            aria-hidden
                            className="absolute inset-y-0 left-0 w-1 bg-violet-500"
                          />
                        ) : null}

                        <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span
                              className={cn(
                                "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
                                isCurrent
                                  ? "border-violet-200 bg-violet-100 text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/15 dark:text-violet-200"
                                  : item.active
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                                  : "border-slate-200 bg-slate-100 text-slate-500 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/45"
                              )}
                            >
                              <KeyRound size={16} />
                            </span>

                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="max-w-full truncate text-[14px] font-semibold tracking-[-0.01em] text-slate-950 dark:text-white">
                                  {item.label || t("agentIA.keysList.unnamed")}
                                </h3>

                                {isCurrent ? (
                                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[9.5px] font-semibold text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-200">
                                    <CheckCircle2 size={11} />
                                    {tp("tokens.current")}
                                  </span>
                                ) : null}

                                {item.active ? (
                                  <StatusBadge active>
                                    {t("agentIA.keysList.statusActive")}
                                  </StatusBadge>
                                ) : (
                                  <StatusBadge active={false}>
                                    {t("agentIA.keysList.statusDisabled")}
                                  </StatusBadge>
                                )}
                              </div>

                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-1.5 xl:shrink-0 xl:justify-end">
                            <CompactActionButton
                              title={
                                isCurrent
                                  ? t("agentIA.keysList.actions.alreadyUsed")
                                  : item.active
                                  ? t("agentIA.keysList.actions.useKey")
                                  : t("agentIA.keysList.actions.reactivateBeforeUse")
                              }
                              busy={isUseBusy}
                              disabled={isRowBusy || isCurrent || !item.active || !item.token}
                              onClick={() => handleUseToken(item)}
                            >
                              <KeyRound size={14} />
                            </CompactActionButton>

                            <CompactActionButton
                              title={t("agentIA.keysList.actions.copyKey")}
                              busy={isCopyBusy}
                              disabled={isRowBusy || !item.token}
                              onClick={() => handleCopyAnyToken(item)}
                            >
                              <Copy size={14} />
                            </CompactActionButton>

                            <CompactActionButton
                              title={tp("tokens.actions.rename")}
                              busy={isEditingThisToken && renamingToken}
                              disabled={isRowBusy || deletingToken}
                              onClick={() => handleRenameToken(item)}
                            >
                              <Pencil size={14} />
                            </CompactActionButton>

                            <CompactActionButton
                              title={
                                item.active
                                  ? t("agentIA.keysList.actions.deactivate")
                                  : t("agentIA.keysList.actions.reactivate")
                              }
                              busy={isToggleBusy}
                              disabled={isRowBusy || deletingToken}
                              onClick={() => handleToggleToken(item)}
                            >
                              {item.active ? <PowerOff size={14} /> : <Wifi size={14} />}
                            </CompactActionButton>

                            <CompactActionButton
                              title={tp("tokens.actions.delete")}
                              tone="danger"
                              busy={isDeletingThisToken && deletingToken}
                              disabled={isRowBusy || renamingToken}
                              onClick={() => handleDeleteToken(item)}
                            >
                              <Trash2 size={14} />
                            </CompactActionButton>
                          </div>
                        </div>

                        <div className="mt-2.5 rounded-xl border border-slate-200/80 bg-slate-50/85 px-3 py-2 dark:border-white/10 dark:bg-[#090f1d]/70">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white text-slate-400 ring-1 ring-slate-200/80 dark:bg-white/[0.06] dark:ring-white/10 dark:text-white/45">
                              <KeyRound size={12} />
                            </span>
                            <code className="min-w-0 flex-1 truncate font-mono text-[11.5px] font-medium text-slate-700 dark:text-white/70">
                              {showToken || isCurrent
                                ? item.token || "—"
                                : maskToken(item.token)}
                            </code>
                          </div>
                        </div>

                        <div className="mt-2 grid gap-2 md:grid-cols-[minmax(0,1.35fr)_minmax(0,0.72fr)_minmax(0,1fr)]">
                          <div
                            className={cn(
                              "min-w-0 rounded-xl border px-3 py-2.5",
                              item.account_bound
                                ? "border-emerald-200/90 bg-emerald-50/70 dark:border-emerald-400/20 dark:bg-emerald-500/[0.08]"
                                : "border-amber-200/90 bg-amber-50/75 dark:border-amber-400/20 dark:bg-amber-500/[0.08]"
                            )}
                            title={
                              item.account_bound
                                ? tp("tokens.binding.boundHint")
                                : tp("tokens.binding.unboundHint")
                            }
                          >
                            <div
                              className={cn(
                                "flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.1em]",
                                item.account_bound
                                  ? "text-emerald-700 dark:text-emerald-300"
                                  : "text-amber-700 dark:text-amber-200"
                              )}
                            >
                              {item.account_bound ? (
                                <ShieldCheck size={12} className="shrink-0" />
                              ) : (
                                <Clock3 size={12} className="shrink-0" />
                              )}
                              <span className="truncate">
                                {item.account_bound
                                  ? tp("tokens.binding.bound")
                                  : tp("tokens.binding.unbound")}
                              </span>
                            </div>

                            {item.account_bound ? (
                              <div className="mt-1 flex min-w-0 items-center gap-1.5 font-mono text-[11.5px] font-medium text-slate-700 dark:text-white/75">
                                <span className="truncate">{boundServer}</span>
                                <span className="shrink-0 text-slate-300 dark:text-white/20">/</span>
                                <span className="shrink-0">{boundLogin}</span>
                              </div>
                            ) : (
                              <p className="mt-1 truncate text-[11px] text-amber-800/80 dark:text-amber-100/70">
                                {tp("tokens.binding.unboundHint")}
                              </p>
                            )}
                          </div>

                          <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.035]">
                            <div className="flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-white/38">
                              <Clock3 size={12} className="shrink-0" />
                              {t("agentIA.keysList.createdLabel")}
                            </div>
                            <div className="mt-1 truncate text-[11.5px] font-medium text-slate-700 dark:text-white/72">
                              {fmtCompactTS(item.created_at)}
                            </div>
                          </div>

                          <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.035]">
                            <div className="flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-white/38">
                              <Activity size={12} className="shrink-0" />
                              {t("agentIA.keysList.lastUsedLabel")}
                            </div>
                            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span className="truncate text-[11.5px] font-medium text-slate-700 dark:text-white/72">
                                {fmtCompactTS(item.last_used_at)}
                              </span>
                              <span className="text-[10px] text-slate-400 dark:text-white/28">·</span>
                              <span className="truncate text-[10px] text-slate-500 dark:text-white/38">
                                {fmtAgo(item.last_used_at, t)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4">
                  <EmptyState>{t("agentIA.keysList.empty")}</EmptyState>
                </div>
              )}

              {/* Le quota reste lié aux données serveur, mais il est maintenant
                  placé sous la liste pour ne plus couper le parcours de lecture. */}
              {serverApiReady ? (
                <div
                  className={cn(
                    "mt-4 flex flex-col gap-2 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
                    tokenLimitReached
                      ? "border-amber-200 bg-amber-50/80 dark:border-amber-400/20 dark:bg-amber-500/[0.08]"
                      : "border-slate-200/90 bg-slate-50/80 dark:border-white/10 dark:bg-white/[0.035]"
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={cn(
                        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
                        tokenLimitReached
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-200"
                          : "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200"
                      )}
                    >
                      <KeyRound size={14} />
                    </span>
                    <span className="truncate text-[13px] font-semibold text-slate-800 dark:text-white/85">
                      {tp("tokens.limit.summary", { count: tokenCount, max: tokenLimit })}
                    </span>
                  </div>

                  <span
                    className={cn(
                      "text-[12px] font-medium",
                      tokenLimitReached
                        ? "text-amber-700 dark:text-amber-200"
                        : "text-slate-500 dark:text-white/45"
                    )}
                  >
                    {tokenLimitReached
                      ? tp("tokens.limit.reached", { max: tokenLimit })
                      : tp("tokens.limit.available", { count: remainingTokenSlots })}
                  </span>
                </div>
              ) : null}

              <p className="mt-3 flex items-center gap-2 text-[12px] text-slate-500 dark:text-white/50">
                <ShieldCheck size={14} className="shrink-0 text-slate-400" />
                {tp("tokens.securityNote")}
              </p>
            </section>
          </div>

          {/* COLONNE LATÉRALE */}
          <aside className="space-y-4">
            {/* Tutoriel vidéo */}
            <section className={cn(shellCard, "p-4")}>
              <AccentTopLine />
              <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-900 dark:text-white">
                <Video size={16} className="text-violet-500" />
                {tp("aside.video.title")}
              </div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-slate-600 dark:text-white/60">
                {tp("aside.video.text")}
              </p>

              <button
                type="button"
                onPointerDown={warmVideoConnection}
                onMouseEnter={warmVideoConnection}
                onFocus={warmVideoConnection}
                onClick={openVideoModal}
                className="group relative mt-3 block w-full overflow-hidden rounded-2xl border border-violet-200/80 bg-slate-950 text-left transition hover:border-violet-300 dark:border-violet-400/20"
              >
                <div className="aspect-video w-full bg-[radial-gradient(circle_at_30%_20%,rgba(139,92,246,0.45),transparent_38%),linear-gradient(135deg,#111827_0%,#0f172a_55%,#020617_100%)]">
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-violet-600/90 text-white ring-4 ring-white/20 transition group-hover:scale-105">
                      <Play size={22} className="ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                </div>
                <span className="pointer-events-none absolute bottom-2 left-2 rounded-lg bg-black/60 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
                  {tp("aside.video.badge")}
                </span>
              </button>

              <button
                type="button"
                onClick={openVideoModal}
                className={cn(btnSoft, "mt-3 w-full")}
              >
                {tp("aside.video.cta")}
                <ArrowRight size={14} />
              </button>
            </section>

            {/* Bon à savoir */}
            <section className={cn(shellCard, "p-4")}>
              <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-900 dark:text-white">
                <Lightbulb size={16} className="text-amber-500" />
                {tp("aside.tip.title")}
              </div>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-600 dark:text-white/60">
                {tp("aside.tip.text")}
              </p>
            </section>

            {/* Problème de connexion */}
            <section className="rounded-[24px] border border-rose-200 bg-rose-50/70 p-4 dark:border-rose-400/20 dark:bg-rose-500/[0.07]">
              <div className="flex items-center gap-2 text-[15px] font-semibold text-rose-700 dark:text-rose-200">
                <LifeBuoy size={16} />
                {tp("aside.support.title")}
              </div>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-rose-900/80 dark:text-rose-100/70">
                {tp("aside.support.text")}
              </p>

              <a
                href={supportWhatsAppHref || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => !supportWhatsAppHref && e.preventDefault()}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-400/30 dark:bg-white/5 dark:text-emerald-300"
              >
                <WhatsAppLogo size={16} />
                {tp("aside.support.cta")}
              </a>

              <p className="mt-2 text-center text-[11.5px] text-rose-900/70 dark:text-rose-100/60">
                {tp("aside.support.note")}
              </p>
            </section>
          </aside>
        </div>
      </div>

      {(flash || err) ? (
        <div className="pointer-events-none fixed right-3 top-3 z-[140] flex w-[min(420px,calc(100vw-1.5rem))] flex-col gap-3 sm:right-5 sm:top-5">
          {flash ? (
            <GlassToast type="success" message={flash} onClose={clearFlash} />
          ) : null}
          {err ? (
            <GlassToast type="error" message={err} onClose={() => setErr("")} />
          ) : null}
        </div>
      ) : null}

      {createTokenModalOpen ? (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm"
        >
          {/* Idem : le nom saisi ne doit pas disparaître sur un clic à côté. */}
          <div
            className={cn(
              "w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-5 ",
              "dark:border-white/10 dark:bg-[#0d1322]"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                    <Sparkles size={18} />
                  </span>
                  <div>
                    <h3 className="text-[17px] font-semibold text-slate-950 dark:text-white">
                      {t("agentIA.createModal.title")}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-white/60">
                      {t("agentIA.createModal.subtitle")}
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeCreateTokenModal}
                disabled={creatingToken}
                className={cn(iconBtn, "h-9 w-9 disabled:cursor-not-allowed disabled:opacity-50")}
                aria-label={t("agentIA.createModal.close")}
                title={t("agentIA.createModal.close")}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-white/42">
                {t("agentIA.createModal.nameLabel")}
              </label>
              <input
                type="text"
                value={tokenLabel}
                onChange={(e) => setTokenLabel(e.target.value)}
                placeholder={t("agentIA.createModal.namePlaceholder")}
                className={inputClass}
                autoFocus
                maxLength={120}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!creatingToken) createServerToken(tokenLabel);
                  }
                }}
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-white/45">
                {t("agentIA.createModal.nameHint")}
              </p>

              <div className="mt-3 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] leading-5 text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100">
                <ShieldCheck size={15} className="mt-0.5 shrink-0" />
                <span>{tp("tokens.binding.createRule")}</span>
              </div>

              {serverApiReady ? (
                <div
                  className={cn(
                    "mt-3 rounded-2xl border px-3 py-2.5 text-[12px]",
                    tokenLimitReached
                      ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100"
                      : "border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-100"
                  )}
                >
                  <span className="font-semibold">
                    {tp("tokens.limit.summary", { count: tokenCount, max: tokenLimit })}
                  </span>
                  <span className="ml-1 opacity-80">
                    · {tokenLimitReached
                      ? tp("tokens.limit.createDisabled", { max: tokenLimit })
                      : tp("tokens.limit.available", { count: remainingTokenSlots })}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeCreateTokenModal}
                disabled={creatingToken}
                className={cn(btnSoft, "w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-50")}
              >
                {t("agentIA.createModal.cancel")}
              </button>
              <button
                type="button"
                onClick={() => createServerToken(tokenLabel)}
                disabled={creatingToken || tokenLimitReached}
                title={tokenLimitReached ? tp("tokens.limit.createDisabled", { max: tokenLimit }) : undefined}
                className={cn(btnPrimary, "w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-70")}
              >
                <Sparkles size={14} className={creatingToken ? "animate-pulse" : ""} />
                {creatingToken ? t("agentIA.createModal.creating") : t("agentIA.createModal.create")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingToken ? (
        <div
          className="fixed inset-0 z-[132] flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-token-title"
        >
          {/* Pas de fermeture au clic sur le fond : un clic à côté effaçait le
              nom en cours de saisie. Sortie par Annuler, la croix ou Échap. */}
          <div
            className={cn(
              "w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-5",
              "dark:border-white/10 dark:bg-[#0d1322]"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                  <Pencil size={18} />
                </span>
                <div className="min-w-0">
                  <h3
                    id="rename-token-title"
                    className="text-[17px] font-semibold text-slate-950 dark:text-white"
                  >
                    {tp("tokens.rename.title")}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-white/60">
                    {tp("tokens.rename.subtitle")}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeRenameTokenModal}
                disabled={renamingToken}
                className={cn(iconBtn, "shrink-0 disabled:cursor-not-allowed disabled:opacity-50")}
                aria-label={tp("tokens.rename.cancel")}
                title={tp("tokens.rename.cancel")}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5">
              <label
                htmlFor="editing-token-label"
                className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-white/42"
              >
                {tp("tokens.rename.nameLabel")}
              </label>
              <input
                id="editing-token-label"
                type="text"
                value={editingTokenLabel}
                onChange={(event) => setEditingTokenLabel(event.target.value)}
                placeholder={tp("tokens.rename.namePlaceholder")}
                className={inputClass}
                autoFocus
                maxLength={120}
                disabled={renamingToken}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (!renamingToken) renameServerToken();
                  }
                }}
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-white/45">
                {tp("tokens.rename.nameHint")}
              </p>

              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-white/40">
                  {tp("tokens.columns.name")}
                </div>
                <div className="mt-1 truncate font-mono text-[12px] text-slate-700 dark:text-white/65">
                  {maskToken(editingToken?.token)}
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeRenameTokenModal}
                disabled={renamingToken}
                className={cn(btnSoft, "w-full sm:w-auto")}
              >
                {tp("tokens.rename.cancel")}
              </button>
              <button
                type="button"
                onClick={renameServerToken}
                disabled={renamingToken || !editingTokenLabel.trim()}
                className={cn(btnPrimary, "w-full sm:w-auto")}
              >
                {renamingToken ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                {renamingToken ? tp("tokens.rename.saving") : tp("tokens.rename.save")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tokenToDelete ? (
        <div
          className="fixed inset-0 z-[133] flex items-center justify-center bg-slate-950/65 px-4 backdrop-blur-sm"
          onClick={closeDeleteTokenModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-token-title"
        >
          <div
            className={cn(
              "w-full max-w-md rounded-[28px] border border-rose-200 bg-white p-5",
              "dark:border-rose-400/20 dark:bg-[#130d18]"
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200">
                  <Trash2 size={18} />
                </span>
                <div className="min-w-0">
                  <h3
                    id="delete-token-title"
                    className="text-[17px] font-semibold text-slate-950 dark:text-white"
                  >
                    {tp("tokens.delete.title")}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-white/60">
                    {tp("tokens.delete.subtitle", {
                      name:
                        tokenToDelete?.label ||
                        tp("tokens.unnamed"),
                    })}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeDeleteTokenModal}
                disabled={deletingToken}
                className={cn(iconBtn, "shrink-0 disabled:cursor-not-allowed disabled:opacity-50")}
                aria-label={tp("tokens.delete.cancel")}
                title={tp("tokens.delete.cancel")}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm leading-6 text-rose-800 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={17} className="mt-1 shrink-0" />
                <span>{tp("tokens.delete.warning")}</span>
              </div>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="truncate text-[13px] font-semibold text-slate-900 dark:text-white">
                {tokenToDelete?.label || tp("tokens.unnamed")}
              </div>
              <div className="mt-1 truncate font-mono text-[12px] text-slate-500 dark:text-white/50">
                {maskToken(tokenToDelete?.token)}
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDeleteTokenModal}
                disabled={deletingToken}
                className={cn(btnSoft, "w-full sm:w-auto")}
              >
                {tp("tokens.delete.cancel")}
              </button>
              <button
                type="button"
                onClick={deleteServerToken}
                disabled={deletingToken}
                className={cn(btnDanger, "h-10 w-full px-4 sm:w-auto")}
              >
                {deletingToken ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                {deletingToken ? tp("tokens.delete.deleting") : tp("tokens.delete.confirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {typeof document !== "undefined"
        ? createPortal(
            <div
              className={cn(
                "fixed inset-y-0 z-[9999] flex items-center justify-center bg-slate-950/92 px-3 py-4 backdrop-blur-xl transition-opacity duration-150 sm:px-5 md:p-6",
                videoModalOpen ? "visible pointer-events-auto opacity-100" : "invisible pointer-events-none opacity-0"
              )}
              style={videoModalOpen ? { left: videoViewport.left, width: videoViewport.width } : { left: -9999, width: "1px" }}
              role="dialog"
              aria-modal={videoModalOpen ? "true" : "false"}
              aria-hidden={videoModalOpen ? "false" : "true"}
              aria-label={t("agentIA.video.modalTitle")}
              onClick={videoModalOpen ? closeVideoModal : undefined}
            >
              <div
                className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-[1180px] flex-col overflow-hidden rounded-[24px] border border-white/10 bg-black  sm:max-h-[calc(100dvh-3rem)] sm:rounded-[30px]"
                style={{ width: "min(100%, 1180px, calc((100dvh - 7rem) * 16 / 9))" }}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-[#060914]/95 px-3 py-3 text-white/90 sm:px-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Video size={16} />
                      {t("agentIA.video.modalTitle")}
                    </div>
            
                  </div>

                  <button
                    type="button"
                    onClick={closeVideoModal}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-white backdrop-blur transition hover:bg-white/15"
                    aria-label={t("agentIA.video.close")}
                    title={t("agentIA.video.closeShort")}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="relative w-full bg-black" style={{ aspectRatio: "16 / 9", maxHeight: "calc(100dvh - 7rem)" }}>
                  {!videoReady && videoModalOpen ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black text-white/80">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <span className="inline-flex h-10 w-10 animate-pulse items-center justify-center rounded-2xl border border-white/10 bg-white/10">
                          <Video size={18} />
                        </span>
                        <span className="text-sm font-medium">{t("agentIA.video.opening")}</span>
                      </div>
                    </div>
                  ) : null}

                  <iframe
                    ref={videoIframeRef}
                    title={t("agentIA.video.modalTitle")}
                    src={HELP_VIDEO_EMBED_MODAL_URL}
                    className="h-full w-full"
                    loading="eager"
                    fetchPriority="high"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    tabIndex={videoModalOpen ? 0 : -1}
                    onLoad={() => setVideoReady(true)}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
